if (!customElements.get('collection-grid')) {
  class CollectionGrid extends HTMLElement {
    constructor() {
      super();

      this.bindOnButtonClick = this.onButtonClick.bind(this);
      this.bindOnPageScroll = this.onPageScroll.bind(this);
    }

    connectedCallback() {
      this.init();
      this.handlePLPScrollRestoration();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.bindOnPageScroll);
    }

    init() {
      if (this.dataset.paginationType === 'pages') return;

      this.loadingPage = false;
      this.currentPage = +this.dataset.currentPage;
      this.productGrid = this.querySelector('#product-grid');
      if (!this.productGrid) return;
      this.paginationElement = this.querySelector('.collection-grid__pagination .product-count');
      this.cardSelector = this.productGrid.classList.contains('grill') ? '.grill__item' : '.grid__item';
      this.cardPageElements = Array.from(this.querySelectorAll(`${this.cardSelector}[data-current-page]`));
      this.currentPages = {};
      this.cardPageElements.forEach((element) => {
        const pageKey = element.dataset.currentPage;

        if (!this.currentPages[pageKey]) {
          this.currentPages[pageKey] = { url: '', page: null, first: null, last: null };
        }

        this.currentPages[pageKey].url = element.dataset.currentPageUrl;
        this.currentPages[pageKey].page = +element.dataset.currentPage;

        if (element.hasAttribute('last-from-page')) {
          this.currentPages[pageKey].last = element;
        } else if (element.hasAttribute('first-from-page')) {
          this.currentPages[pageKey].first = element;
        }
      });

      this.automaticTriggers = this.querySelectorAll('.collection-grid__automatic');

      if (this.dataset.paginationType === 'click') {
        this.prevButton = this.querySelector('.collection-grid__prev');
        this.nextButton = this.querySelector('.collection-grid__next');

        if (this.prevButton) this.prevButton.addEventListener('click', this.bindOnButtonClick);
        if (this.nextButton) this.nextButton.addEventListener('click', this.bindOnButtonClick);
      }

      this.checkAutomaticTriggers();
      window.addEventListener('scroll', this.bindOnPageScroll);
    }

    loadPage(targetUrl, trigger) {
      if (!targetUrl) {
        this.loadingPage = false;
      }
      this.loadingPage = true;

      fetch(targetUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');

          const currentScroll = document.documentElement.scrollHeight - window.scrollY;
          let newProductCards = Array.from(responseHTML.querySelectorAll(`collection-grid ${this.cardSelector}`));
          const newPaginationElement = responseHTML.querySelector('.collection-grid__pagination .product-count');
          let newTrigger = responseHTML.getElementById(trigger.id);
          if (trigger.id == 'PrevPageTrigger') {
            newProductCards = newProductCards.reverse();
            if (this.hasAttribute('data-cascade')) {
              newProductCards = newProductCards.map((card, index) => {
                card.style.setProperty('--animation-order', index);
                return card;
              });
            }
          }
          newProductCards.forEach((card) => {
            if (trigger.id == 'PrevPageTrigger') {
              this.productGrid.prepend(card);
            } else if (trigger.id == 'NextPageTrigger') {
              this.productGrid.appendChild(card);
            }
          });

          if (trigger.id == 'PrevPageTrigger') {
            window.scrollTo({
              top: document.documentElement.scrollHeight - currentScroll,
              behavior: 'instant', // Prevents unwanted animation effects
            });
          }

          // window.history.replaceState({}, '', targetUrl);
          // this.currentPage = +trigger.dataset.targetPage;

          if (newTrigger) {
            newTrigger.addEventListener('click', this.bindOnButtonClick);
            trigger.replaceWith(newTrigger);
          } else {
            trigger.remove();
          }

          if (newPaginationElement && this.paginationElement && trigger.id != 'PrevPageTrigger') {
            this.paginationElement.innerHTML = newPaginationElement.innerHTML;
          }
        })
        .catch((err) => {
          console.error(err);
          if (trigger) this.setLoadingTrigger(trigger, false);
        })
        .finally(() => {
          this.cardPageElements = Array.from(this.querySelectorAll(`${this.cardSelector}[data-current-page]`));
          this.currentPages = {};
          this.cardPageElements.forEach((element) => {
            const pageKey = element.dataset.currentPage;

            if (!this.currentPages[pageKey]) {
              this.currentPages[pageKey] = { url: '', first: null, last: null };
            }

            this.currentPages[pageKey].url = element.dataset.currentPageUrl;
            this.currentPages[pageKey].page = +element.dataset.currentPage;

            if (element.hasAttribute('last-from-page')) {
              this.currentPages[pageKey].last = element;
            } else if (element.hasAttribute('first-from-page')) {
              this.currentPages[pageKey].first = element;
            }
          });

          this.paginationElement = this.querySelector('.collection-grid__pagination .product-count');

          this.automaticTriggers = this.querySelectorAll('.collection-grid__automatic');
          if (trigger) this.setLoadingTrigger(trigger, false);

          this.loadingPage = false;
        });
    }

    onButtonClick(event) {
      event.preventDefault();
      const button = event.currentTarget;

      this.setLoadingTrigger(button, true);
      this.loadPage(button.dataset.target, button);
    }

    setLoadingTrigger(opener, enableLoading) {
      if (enableLoading) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');
      } else {
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        opener.querySelector('.loading__spinner').classList.add('hidden');
      }
    }

    checkAutomaticTriggers() {
      if (!this.loadingPage) {
        this.automaticTriggers.forEach((trigger) => {
          if (trigger.getBoundingClientRect().top > 0 && trigger.getBoundingClientRect().bottom < window.innerHeight) {
            this.setLoadingTrigger(trigger, true);
            this.loadPage(trigger.dataset.target, trigger);
          }
        });
      }
    }

    onPageScroll() {
      Object.entries(this.currentPages).forEach(([pageKey, page]) => {
        if (this.currentPage != page.page) {
          if (
            window.scrollY >= page.first.offsetTop &&
            window.scrollY < page.last.offsetTop + page.last.getBoundingClientRect().height
          ) {
            // window.history.replaceState({}, '', page.url);
            const url = new URL(window.location.href);
            const params = url.searchParams;
            params.set('page', `${page.page}`);
            url.search = params.toString();
            window.history.replaceState({ page: page.page }, '', url);
            this.currentPage = page.page;
          }
        }
      });

      this.checkAutomaticTriggers();
    }
    
    handlePLPScrollRestoration() {
      const isSafariMobile =
        /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
        /iPhone|iPad|iPod/i.test(navigator.userAgent);
      
      if (!isSafariMobile) return;

      window.addEventListener('beforeunload', () => {
        sessionStorage.setItem('plp-scroll', window.scrollY);
      });

      document.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (link && link.href && link.href.indexOf('/products/') !== -1) {
          sessionStorage.setItem('plp-scroll', window.scrollY);
        }
      });

      window.addEventListener('pageshow', (event) => {
        const scroll = sessionStorage.getItem('plp-scroll');
        if (event.persisted && scroll !== null) {
          setTimeout(() => {
            window.scrollTo(0, parseInt(scroll, 10));
          }, 100);
          sessionStorage.removeItem('plp-scroll');
        } else if (!event.persisted) {
          sessionStorage.removeItem('plp-scroll');
        }
      });
    }
  }

  customElements.define('collection-grid', CollectionGrid);
}
